import { useEffect, useState } from 'react'
import { PartnerLogoImage, partners } from '#app/components/partner-logos.tsx'
import { cn } from '#app/utils/misc.tsx'

/** How long the spotlight rests on one logo. */
const interval = 1700

/**
 * LogoSpotlight: the partner logos in one row, with a soft brand glow that
 * moves from logo to logo every 1.7s. The active logo shows full colour and
 * slightly larger, the others muted, and a caption below names it with its
 * kind. Hovering or focusing a logo holds the spotlight on it.
 *
 * The server and reduced motion render a static row of full logos without
 * the caption; the cycling starts once hydrated.
 */
export function LogoSpotlight({
	labels,
}: {
	/** The caption's word for each kind, see Kunde and Partner in CONTEXT.md */
	labels: Record<'kunde' | 'partner', string>
}) {
	const [still, setStill] = useState(true)
	const [active, setActive] = useState(0)
	const [paused, setPaused] = useState(false)

	useEffect(() => {
		setStill(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
	}, [])

	useEffect(() => {
		if (still || paused) return
		const id = setInterval(
			() => setActive((a) => (a + 1) % partners.length),
			interval,
		)
		return () => clearInterval(id)
	}, [still, paused])

	const current = partners[active] ?? partners[0]

	function hold(index: number) {
		setActive(index)
		setPaused(true)
	}

	return (
		<div data-motion="logo-spotlight">
			<ul className="flex flex-wrap items-center justify-center gap-x-14 gap-y-10">
				{partners.map((partner, i) => {
					const lit = !still && i === active
					const on = still || lit
					return (
						<li key={partner.id} className="relative">
							<span
								aria-hidden="true"
								className={cn(
									'bg-brand-soft duration-long absolute -inset-6 rounded-full blur-2xl transition-opacity',
									lit ? 'opacity-100' : 'opacity-0',
								)}
							/>
							<a
								href={partner.url}
								target="_blank"
								rel="noopener noreferrer"
								title={partner.name}
								data-active={on}
								onPointerEnter={() => hold(i)}
								onPointerLeave={() => setPaused(false)}
								onFocus={() => hold(i)}
								onBlur={() => setPaused(false)}
								className={cn(
									'group focus-cosy ease-out-expo duration-medium relative block min-h-[44px] content-center rounded-sm transition-[opacity,scale]',
									on ? 'scale-110 opacity-100' : 'opacity-40',
								)}
							>
								<PartnerLogoImage partner={partner} lit={lit} />
							</a>
						</li>
					)
				})}
			</ul>
			{/* The line is reserved from the server render on, so the caption
			    appearing after hydration shifts nothing. */}
			<div className="text-body-sm mt-10 min-h-lh">
				{!still && (
					<p
						key={current.id}
						data-slot="logo-spotlight-caption"
						className="animate-rise-in text-muted-foreground text-center"
					>
						<span className="text-foreground font-bold">{current.name}</span>
						{' · '}
						{labels[current.kind]}
					</p>
				)}
			</div>
		</div>
	)
}
